import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import DomainSelector from './DomainSelector';
import FrameworkExamples from './FrameworkExamples';
import './AIConfigCreator.css';

interface AIConfigCreatorProps {
  configType?: 'task' | 'domain' | 'safety';
  onConfigCreated: (config: any) => void;
  onCancel: () => void;
}

const AIConfigCreator: React.FC<AIConfigCreatorProps> = ({ configType: initialType = 'task', onConfigCreated, onCancel }) => {
  const [description, setDescription] = useState('');
  const [configName, setConfigName] = useState('');
  const [configType, setConfigType] = useState(initialType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editablePreview, setEditablePreview] = useState<any>(null);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [primaryDomain, setPrimaryDomain] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [existingNames, setExistingNames] = useState<string[]>([]);

  // Load existing framework names for validation
  useEffect(() => {
    const loadExistingNames = async () => {
      try {
        const configs = await knowledgeBaseApi.getAllConfigs();
        const names = configs[configType]?.map((c: any) => c.name || c.config_name) || [];
        setExistingNames(names);
      } catch (err) {
        console.error('Failed to load existing configs:', err);
      }
    };
    loadExistingNames();
  }, [configType]);

  // Validate name as user types
  useEffect(() => {
    if (configName.trim()) {
      const normalizedName = configName.toLowerCase().replace(/\s+/g, '_');
      if (existingNames.some(name => name.toLowerCase().replace(/\s+/g, '_') === normalizedName)) {
        setNameError(`A ${configType} with this name already exists`);
      } else {
        setNameError(null);
      }
    } else {
      setNameError(null);
    }
  }, [configName, existingNames, configType]);

  const handleExampleSelect = (example: any) => {
    setConfigName(example.name);
    setSelectedDomains(example.domains);
    setPrimaryDomain(example.primaryDomain);
    setDescription(
      `Create a framework for ${example.description.toLowerCase()}. ` +
      `Key steps include: ${example.sampleSteps.join('; ')}`
    );
  };

  const handleCreate = async () => {
    if (!description.trim() || !configName.trim()) {
      setError('Please provide both a description and a name for the configuration');
      return;
    }

    if (nameError) {
      setError(nameError);
      return;
    }

    if (configType === 'task' && selectedDomains.length === 0) {
      setError('Please select at least one domain for this task framework');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Include domain information in the description for task frameworks
      let enhancedDescription = description;
      if (configType === 'task' && selectedDomains.length > 0) {
        enhancedDescription = `${description}\n\nDomains: ${selectedDomains.join(', ')}\nPrimary Domain: ${primaryDomain}`;
      }

      const result = await knowledgeBaseApi.createConfigWithAI(
        enhancedDescription,
        configType,
        configName.toLowerCase().replace(/\s+/g, '_')
      );

      if (result.success) {
        // Ensure domains are set correctly if provided
        if (configType === 'task' && selectedDomains.length > 0) {
          result.config.domains = selectedDomains;
          result.config.primary_domain = primaryDomain;
        }
        setPreview(result.config);
        setEditablePreview(JSON.stringify(result.config, null, 2));
        setShowPreview(true);
      } else {
        setError('Failed to create configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Error creating configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    try {
      const finalConfig = JSON.parse(editablePreview);
      onConfigCreated(finalConfig);
    } catch (err) {
      setError('Invalid JSON format in preview. Please fix the syntax.');
    }
  };

  const handleEdit = () => {
    setShowPreview(false);
    // Keep the description and name for editing
  };

  return (
    <div className="ai-config-creator">
      {!showPreview ? (
        <>
          <h3>Create {configType === 'task' ? 'Task Framework' : configType === 'domain' ? 'Domain Rules' : 'Safety Checks'} with AI</h3>
          
          {/* Guidance section for domain rules */}
          {configType === 'domain' && (
            <div className="guidance-box" style={{ 
              backgroundColor: '#f0f7ff', 
              border: '1px solid #0066cc', 
              borderRadius: '4px', 
              padding: '12px', 
              marginBottom: '20px' 
            }}>
              <strong>Domain Rules Guidance:</strong>
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li><strong>If you have specific rules:</strong> List them clearly and AI will convert to JSON format</li>
                <li><strong>If you want AI to create rules:</strong> Describe the domain and requirements broadly</li>
                <li>Include regulatory requirements (GAAP, tax codes, etc.) but keep them conceptual</li>
                <li>Focus on WHAT rules apply, not HOW to perform specific tasks</li>
                <li>Mention key calculations, thresholds, and decision criteria</li>
                <li>Avoid step-by-step procedures - those belong in task frameworks</li>
              </ul>
            </div>
          )}
          
          {/* Enhanced guidance section for task frameworks */}
          {configType === 'task' && (
            <>
              <div className="guidance-box" style={{ 
                backgroundColor: '#f0fff0', 
                border: '1px solid #00cc00', 
                borderRadius: '4px', 
                padding: '12px', 
                marginBottom: '20px' 
              }}>
                <strong>Task Framework Structure:</strong>
                <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                  <li><strong>Semantic Context:</strong> Keywords and scenarios that trigger this task</li>
                  <li><strong>Processing Approach:</strong> Step-by-step procedures with validation points</li>
                  <li><strong>Validation Points:</strong> Use MUST/CANNOT/ALWAYS for critical checks</li>
                  <li><strong>Troubleshooting Guide:</strong> Common issues and resolutions</li>
                  <li><strong>Industry Variations:</strong> How the task differs across industries</li>
                </ul>
                <div style={{ marginTop: '12px', padding: '8px', background: '#e6ffe6', borderRadius: '4px' }}>
                  <strong>💡 Tip:</strong> The more specific you are about steps, validations, and edge cases, the better the framework will be.
                </div>
              </div>

              <FrameworkExamples onSelectExample={handleExampleSelect} />
            </>
          )}
          
          <div className="form-group">
            <label htmlFor="config-name">
              {configType === 'task' ? 'Framework' : configType === 'domain' ? 'Rules' : 'Checks'} Name
              {nameError && <span className="name-error"> - {nameError}</span>}
            </label>
            <input
              id="config-name"
              type="text"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              placeholder={configType === 'task' 
                ? "e.g., Monthly Sales Tax Calculation" 
                : configType === 'domain'
                ? "e.g., Revenue Recognition Rules"
                : "e.g., Data Validation Checks"}
              disabled={loading}
              className={nameError ? 'error' : ''}
            />
            <small className="help-text">
              Use descriptive names. Will be converted to lowercase with underscores.
            </small>
          </div>

          {/* Domain selector for task frameworks */}
          {configType === 'task' && (
            <DomainSelector
              selectedDomains={selectedDomains}
              onDomainsChange={setSelectedDomains}
              primaryDomain={primaryDomain}
              onPrimaryDomainChange={setPrimaryDomain}
              disabled={loading}
            />
          )}

          {/* Hide type selector when type is preset */}
          {!initialType && (
            <div className="form-group">
              <label htmlFor="config-type">Configuration Type</label>
              <select
                id="config-type"
                value={configType}
                onChange={(e) => setConfigType(e.target.value as 'task' | 'domain' | 'safety')}
                disabled={loading}
              >
                <option value="task">Task Framework</option>
                <option value="domain">Domain Rules</option>
                <option value="safety">Safety Checks</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">
              Describe the {configType === 'task' ? 'framework' : configType === 'domain' ? 'domain rules' : 'safety checks'} in natural language
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                configType === 'task' 
                  ? "Example: Create a framework for calculating and reporting monthly sales tax. It should handle multiple tax jurisdictions, exemptions for certain product categories, and generate reports showing tax collected by state. Include validation to ensure tax rates are current and calculations match expected totals."
                : configType === 'domain'
                  ? "Option 1 (AI creates): Define payroll domain rules covering wage calculations, tax withholdings, and benefit deductions.\n\nOption 2 (Your rules): Regular hours: 40/week max standard time. Overtime: Time and half (1.5x) for hours 41-60. Double time: 2x rate for hours over 60 or holidays. Minimum wage: $15/hour or state minimum if higher. Pay periods: Bi-weekly on Friday. Tax withholding: Follow IRS Publication 15 tables..."
                  : "Example: Define validation checks for journal entries. Include rules for debit/credit balance, account restrictions, approval thresholds, and period controls. Focus on what needs to be validated, not how to implement the checks."
              }
              rows={8}
              disabled={loading}
            />
            <small className="help-text">
              {configType === 'task' 
                ? "Be specific about the steps, inputs, outputs, and validations needed. The AI will create a detailed framework for executing this specific task."
                : configType === 'domain'
                ? (
                  <>
                    <strong>Two approaches:</strong><br/>
                    <strong>1. Let AI create rules:</strong> Describe the domain broadly (e.g., "payroll processing rules") and AI will generate comprehensive rules.<br/>
                    <strong>2. Convert your rules to JSON:</strong> List your specific rules (e.g., "Overtime is 1.5x after 40 hours, Double time on holidays...") and AI will structure them into JSON format.<br/>
                    <em>Focus on WHAT rules govern this area, not HOW to execute tasks.</em>
                  </>
                )
                : "Describe what needs to be validated and why. Focus on business rules and compliance requirements that need to be checked."
              }
            </small>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="button-group">
            <button
              onClick={handleCreate}
              disabled={loading || !description.trim() || !configName.trim()}
              className="btn btn-primary"
            >
              {loading ? 'Creating...' : 'Generate Configuration'}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>Preview & Edit Generated Configuration</h3>
          
          <div className="config-preview">
            <div className="preview-header">
              <h4>{preview.name || configName}</h4>
              <p className="preview-type">Type: {configType}</p>
              {configType === 'task' && preview.domains && (
                <p className="preview-domains">
                  Domains: {preview.domains.join(', ')} | Primary: {preview.primary_domain}
                </p>
              )}
            </div>
            
            <div className="preview-edit-section">
              <label>Edit the JSON below to make adjustments:</label>
              <textarea
                className="json-editor"
                value={editablePreview}
                onChange={(e) => setEditablePreview(e.target.value)}
                rows={20}
                spellCheck={false}
              />
              <small className="help-text">
                💡 You can edit the JSON directly. Make sure to maintain valid JSON syntax.
              </small>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="button-group">
            <button
              onClick={handleConfirm}
              className="btn btn-primary"
            >
              Save Configuration
            </button>
            <button
              onClick={handleEdit}
              className="btn btn-secondary"
            >
              Back to Edit
            </button>
            <button
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AIConfigCreator;